import { cx } from '@emotion/css';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import DatePicker from 'react-datepicker';

import { dateOnlySlashesFormat, formatDate } from 'App/services/DateService';
import dayjs from 'Shared/dayjs';
import asFormField from './asFormField';

export const DatePickerInput = ({
    name,
    value,
    onChange,
    onBlur,
    className,
    style,

    disabled,
    placeholder,
    filterDate,
    minDate,
    maxDate,
    debounce: enableDebounce,
}) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedOnChange = useCallback(debounce(onChange, 200), [onChange]);
    const onChangeFn = enableDebounce ? debouncedOnChange : onChange;

    const handleChange = (event, rawEvent) => {
        const rawValue = rawEvent.target.value;
        // When typing in a date, datepicker sends string rawValue, ignore here and process via handleChangeRaw
        if (typeof rawValue === 'string') {
            return;
        }
        onChange(event, rawEvent);
    };

    const handleChangeRaw = (event) => {
        const newValue = event.target.value;
        // When clicking calendar date, datepicker sends undefined before the actual value, undefined should be ignored
        if (newValue === undefined) {
            return;
        }
        if (
            !newValue ||
            newValue.length <= 7 ||
            newValue.match(/^\d{4}\/\d{2}\/\d?$/) ||
            newValue.match(/^\d{2}\/\d{2}\/\d{3}$/)
        ) {
            return onChangeFn(null);
        }
        const newDate = dayjs(newValue, [dateOnlySlashesFormat, 'YYYY/MM/DD', 'MM-DD-YYYY', 'YYYY-MM-DD'], true);
        if (!newDate.isValid()) {
            return onChangeFn(null);
        }
        return onChangeFn(newDate.toDate());
    };

    return (
        <DatePicker
            id={name}
            name={name}
            selected={value}
            onChange={handleChange}
            onChangeRaw={handleChangeRaw}
            onBlur={onBlur}
            className={cx('form-control', className)}
            style={style}
            // react-datepicker uses date-fns format tokens, which differ from dayjs (DD -> dd, YYYY -> yyyy)
            dateFormat="MM/dd/yyyy"
            placeholderText={placeholder}
            filterDate={filterDate}
            disabled={disabled}
            minDate={minDate}
            maxDate={maxDate}
            showMonthDropdown
            showYearDropdown
            scrollableYearDropdown
            yearDropdownItemNumber={14}
        />
    );
};

DatePickerInput.propTypes = {
    name: PropTypes.string.isRequired,
    value: PropTypes.instanceOf(Date),
    onChange: PropTypes.func.isRequired,
    onBlur: PropTypes.func,
    className: PropTypes.string,
    style: PropTypes.shape({}),

    placeholder: PropTypes.string,
    disabled: PropTypes.bool,
    filterDate: PropTypes.func,
    minDate: PropTypes.instanceOf(Date),
    maxDate: PropTypes.instanceOf(Date),
    debounce: PropTypes.bool,
};

DatePickerInput.defaultProps = {
    value: null,
    onBlur: null,
    className: '',
    style: null,

    placeholder: dateOnlySlashesFormat,
    disabled: false,
    filterDate: undefined,
    minDate: null,
    maxDate: null,
    debounce: false,
};

export const DatePickerField = asFormField({
    WrappedComponent: React.memo(DatePickerInput),
    onValidateField: (value, { minDate, maxDate }) => {
        if (value && dayjs(value).isBefore(dayjs('1900/01/01'))) {
            return 'Cannot select date prior to 1900';
        }
        if (value && minDate && dayjs(value).isBefore(dayjs(minDate), 'day')) {
            return `Date cannot be before ${formatDate(minDate)}`;
        }
        if (value && maxDate && dayjs(value).isAfter(dayjs(maxDate), 'day')) {
            return `Date cannot be after ${formatDate(maxDate)}`;
        }
    },
});
